import { PHASE, PLAYER } from "../../../common/constants.ts";
import type { Player, TradeTeams } from "../../../common/types.ts";
import { idb } from "../../db/index.ts";
import { g, local } from "../../util/index.ts";
import { player } from "../index.ts";
import {
	isPitcherCapable,
	planRoster,
	planRosterAddition,
	rankRosterCandidates,
	realPositionsProtected,
	rosterImprovement,
} from "./rosterComposition.baseball.ts";
import { teamBaseballBudget } from "./aiFinances.baseball.ts";
import getPayroll from "./getPayroll.ts";
import rosterAutoSort from "./rosterAutoSort.ts";
import { ValueChangeCalculator } from "./ValueChangeCalculator.ts";
import isUntradable from "../trade/isUntradable.ts";
import makeItWork from "../trade/makeItWork.ts";
import processTrade from "../trade/processTrade.ts";
import summary from "../trade/summary.ts";
import { organizationPlayers, ownerTid } from "../trade/organizationPlayers.ts";
import { minorLeaguePlan, minorMarketReviewDue, type MinorMarketPlayer } from "./minorLeaguePlan.baseball.ts";
import { majorLeagueBenchmarks } from "../player/minorsSatisfaction.ts";
import { rememberMinorLeagueCallup } from "../player/minorsSatisfaction.ts";
import {
	candidateTraits,
	hasShortage,
	minorsStatus,
	quickReduction,
	type CandidateTraits,
	type MinorsStatus,
} from "./minorsComposition.baseball.ts";
import {
	contractContext,
	contractMonth,
	compensationProspects,
	contractWindow,
	extraDumpAllowed,
	dayStamp,
	decideContract,
	judgeFinancialTrade,
	maxCompensationAssets,
	payrollCost,
	MAX_WINDOW_RUNS,
	WINDOW_SPREAD_DAYS,
	DEADLINE_WINDOW_DAYS,
	PRESSURE_PAYROLL,
	PROBLEM_SCORE,
	RESALE_PROTECTION_DAYS,
	recentFormerTeams,
	recentlyTraded,
	type ContractWatch,
	type ContractWatchEntry,
	type ContractWindow,
	type WatchedTeam,
} from "./contractMarket.baseball.ts";

const dh = () => g.get("dh") !== "none";
const age = (p: Player) => g.get("season") - p.born.year;

// How many surplus players a club shops around per roster check. Trading is the
// expensive part, so only the ones another club would actually want get offered.
const MAX_TRADE_ATTEMPTS = 6;
const MAX_TRADES = 3;
const MIN_NEED_GAIN = 3;
const MAX_SURPLUS_SHOPPING_ATTEMPTS = 2;

type ContractTradeProfile = {
	marketRatio: number;
	remainingYears: number;
	recentWar: number;
	warTrend: number;
	rosterImpact: number;
	problemScore: number;
};

// Compare salaries against current players with similar role, rating and age. This
// follows league economics automatically and never treats a dollar amount as bad by itself.
export const profileProblemContract = (
	p: Player,
	peers: Player[],
	rosterImpact: number,
	season: number,
): ContractTradeProfile => {
	const rating = p.ratings.at(-1)!;
	const pos = rating.pos;
	const ovr = rating.ovrs?.[pos] ?? rating.ovr;
	const playerAge = season - p.born.year;
	const comparable = peers.filter((peer) => {
		if (
			peer.pid === p.pid ||
			peer.contract.amount <= 0 ||
			!(peer.tid >= 0 || peer.tid === PLAYER.FREE_AGENT)
		)
			return false;
		const peerRating = peer.ratings.at(-1);
		if (!peerRating) return false;
		const pr = peerRating;
		const peerPos = pr.pos;
		const peerOvr = pr.ovrs?.[pos] ?? (peerPos === pos ? pr.ovr : 0);
		const sameRole =
			pos === "SP" || pos === "RP"
				? peerPos === pos || (pos === "RP" && peerPos === "SP")
				: peerPos === pos;
		return (
			sameRole &&
			Math.abs(peerOvr - ovr) <= 7 &&
			Math.abs(season - peer.born.year - playerAge) <= 4
		);
	});
	const salaries = comparable
		.map((peer) => peer.contract.amount)
		.sort((a, b) => a - b);
	const median = salaries.length > 0 ? salaries[Math.floor((salaries.length - 1) / 2)]! : 0;
	const marketRatio = median > 0 ? p.contract.amount / median : 1;
	const completed = (p.stats ?? []).filter(
		(row) => !row.playoffs && row.season < season && Number.isFinite(row.war),
	).sort((a, b) => a.season - b.season);
	const recent = completed.slice(-3).map((row) => row.war as number);
	const recentWar = recent.length
		? recent.reduce((sum, war) => sum + war, 0)
		: 0;
	const warTrend = recent.length >= 2 ? recent.at(-1)! - recent[0]! : 0;
	const remainingYears = Math.max(0, p.contract.exp - season + 1);
	const marketPenalty = Math.max(0, Math.log2(Math.max(1, marketRatio)));
	const performancePenalty =
		recent.length >= 2 && recentWar < 0
			? Math.min(2, Math.abs(recentWar) / 2)
			: 0;
	const trendPenalty = warTrend < -1 ? Math.min(1, Math.abs(warTrend) / 4) : 0;
	const yearsPenalty = Math.min(1, Math.max(0, remainingYears - 1) / 3);
	const lowRoleImpact = Math.max(0, 1 - rosterImpact / 24);
	const reboundCredit = rating.pot > rating.ovr + 8 ? 0.6 : 0;
	const problemScore = Math.max(
		0,
		marketPenalty +
			performancePenalty +
			trendPenalty +
			yearsPenalty +
			lowRoleImpact -
			reboundCredit,
	);
	return {
		marketRatio,
		remainingYears,
		recentWar,
		warTrend,
		rosterImpact,
		problemScore,
	};
};

// After the playoffs the game's season is still the one just finished; its salaries are
// no longer a decision, so payroll checks start with the next season.
export const firstPayrollSeason = () =>
	g.get("phase") > PHASE.PLAYOFFS ? g.get("season") + 1 : g.get("season");

// Effective change in what the club pays, season by season, if these players leave and
// those arrive: released players already on the books, the minimum payroll top-up and
// the luxury tax all included.
export const effectiveSavings = async (
	tid: number,
	outgoing: Player[],
	incoming: Player[],
) => {
	const first = firstPayrollSeason();
	const last = Math.max(
		first,
		...outgoing.map((p) => p.contract.exp),
		...incoming.map((p) => p.contract.exp),
	);
	let now = 0;
	let total = 0;
	for (let season = first; season <= last; season++) {
		const before = await getPayroll(tid, season);
		let after = before;
		for (const p of incoming) if (p.contract.exp >= season) after += p.contract.amount;
		for (const p of outgoing) if (p.contract.exp >= season) after -= p.contract.amount;
		const current = season === first;
		const saving = payrollCost(before, current) - payrollCost(after, current);
		if (season === first) now = saving;
		total += saving;
	}
	return { now, total };
};

export const tradePayrollFits = async (
	tid: number,
	pids: number[],
	outPids: number[],
	budget: number,
) => {
	const incoming = (await Promise.all(pids.map((pid) => idb.cache.players.get(pid)))).filter(
		(p): p is Player => p !== undefined,
	);
	const outgoing = (await Promise.all(outPids.map((pid) => idb.cache.players.get(pid)))).filter(
		(p): p is Player => p !== undefined,
	);
	const firstSeason = firstPayrollSeason();
	const lastSeason = Math.max(
		firstSeason,
		...incoming.map((p) => p.contract.exp),
		...outgoing.map((p) => p.contract.exp),
	);
	for (let season = firstSeason; season <= lastSeason; season++) {
		const before = await getPayroll(tid, season);
		let after = before;
		for (const p of incoming) if (p.contract.exp >= season) after += p.contract.amount;
		for (const p of outgoing) if (p.contract.exp >= season) after -= p.contract.amount;
		// A club already above plan can still make a trade that lowers its obligations.
		if (after > budget && after >= before) return false;
	}
	return true;
};

// The Gui's rule: at this age a CPU club's minor leaguer either makes the big club or
// gets traded. Nobody waits in the farm system past it, which keeps the trade market busy.
export const MINORS_MAX_AGE = 25;
const minorsEligible = (p: Player) => age(p) < MINORS_MAX_AGE;

const isAiTeam = (tid: number) =>
	!g.get("userTids").includes(tid) ||
	local.autoPlayUntil !== undefined ||
	g.get("spectator");

// AI clubs trade outside the playoffs, before the deadline and away from the draft itself
const tradesAllowed = () => {
	const phase = g.get("phase");
	return (
		!g.get("forceHistoricalRosters") &&
		((phase >= PHASE.PRESEASON && phase <= PHASE.REGULAR_SEASON) ||
			(phase >= PHASE.AFTER_DRAFT && phase <= PHASE.FREE_AGENCY))
	);
};

const sendToMinors = async (p: Player) => {
	// player.sign already places very young players in the minors; sending them again would overwrite their
	// organization with PLAYER.MINORS and leave them in nobody's system
	if (p.tid === PLAYER.MINORS) {
		await idb.cache.players.put(p);
		return;
	}
	p.minorTid = p.tid;
	p.tid = PLAYER.MINORS;
	await idb.cache.players.put(p);
};

// Shop a surplus player to the clubs he would actually help. The buyer pays with what
// it can spare (picks, prospects or a player the seller needs) and judges the offer
// with the same valuation as any other AI trade; nobody is forced to take him.
// Financial mode (shopRosterSurplus): the trade must really lower the seller's
// obligations, the planned roster with the incoming players must still justify it,
// and buyers are screened for need, strategy and payroll in every contract year.
type FinancialTradeOptions = {
	problemScore: number;
	compensation: boolean;
	// Salary still owed as a share of the club's plan (sizes the compensation)
	materialShare: number;
	trace?: string[];
};

// The package search, held in an object so tests can replace the valuation side
export const tradeSearch = { makeItWork };

const remainingSalary = (players: Player[], season: number) =>
	players.reduce(
		(sum, p2) =>
			sum + p2.contract.amount * Math.max(0, p2.contract.exp - season + 1),
		0,
	);

const tradeSurplus = async (
	p: Player,
	keep: Player[],
	size: number,
	valueChangeCalculator: ValueChangeCalculator,
	financial?: FinancialTradeOptions,
) => {
	if (isUntradable(p).untradable) {
		return false;
	}
	const tid = ownerTid(p)!;
	const season = g.get("season");
	const trace = financial?.trace;
	const formerTeams = financial ? recentFormerTeams(p, season) : new Set<number>();
	const now = financial ? (await marketCalendar()).now : 0;
	const teams = await idb.cache.teams.getAll();
	const buyers = [];
	for (const t of teams) {
		if (t.disabled || t.tid === tid || !isAiTeam(t.tid)) {
			continue;
		}
		const roster = await idb.cache.players.indexGetAll("playersByTid", t.tid);
		const gain = rosterImprovement(roster, p, g.get("maxRosterSize"), dh());
		if (gain < MIN_NEED_GAIN) {
			if (trace && gain > 0) trace.push(`${t.abbrev}: ganho ${gain.toFixed(1)} abaixo da necessidade mínima`);
			continue;
		}
		if (formerTeams.has(t.tid)) {
			trace?.push(`${t.abbrev}: trocou o jogador recentemente (proteção contra revenda)`);
			continue;
		}
		let absorbs = true;
		let rebuildingVeteran = false;
		if (financial) {
			// Without room in some contract year the buyer must send salary back
			absorbs = await tradePayrollFits(t.tid, [p.pid], [], await teamBaseballBudget(t.tid));
			// A rebuilding club only takes a veteran's remaining years with compensation
			rebuildingVeteran =
				t.strategy === "rebuilding" && age(p) >= 29 && p.contract.exp > season;
			trace?.push(
				`${t.abbrev} (${t.strategy}): ganho ${gain.toFixed(1)}${absorbs ? "" : ", sem orçamento para absorver o salário em algum ano"}${rebuildingVeteran ? `, reconstrução: só com compensação${financial.compensation ? "" : " (o vendedor não tem pressão para pagá-la): recusa"}` : ""}`,
			);
			if (rebuildingVeteran && !financial.compensation) continue;
		}
		buyers.push({ tid: t.tid, abbrev: t.abbrev, gain, absorbs, rebuildingVeteran });
	}
	buyers.sort((a, b) => b.gain - a.gain);

	for (const { tid: otherTid, abbrev, rebuildingVeteran } of buyers.slice(0, 3)) {
		// The seller is cutting players, so the only active players worth taking back
		// are ones that fill a hole in what it keeps
		const pidsExcluded = (
			await idb.cache.players.indexGetAll("playersByTid", otherTid)
		)
			.filter(
				(p2) =>
					rosterImprovement(keep, p2, size, dh()) < MIN_NEED_GAIN ||
					(financial !== undefined && recentlyTraded(p2, now)),
			)
			.map((p2) => p2.pid);

		const teams0: TradeTeams = [
			{ tid, pids: [p.pid], dpids: [], pidsExcluded: [], dpidsExcluded: [] },
			{ tid: otherTid, pids: [], dpids: [], pidsExcluded, dpidsExcluded: [] },
		];
		const clubMinors = (
			await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS)
		).filter((prospect) => prospect.minorTid === tid);
		const clubProspects = clubMinors.filter(
			(prospect) =>
				minorsEligible(prospect) &&
				// A prospect just received is not passed on again as compensation
				(financial === undefined || !recentlyTraded(prospect, now)),
		);
		// Moving salary never costs a good prospect: only the lower half is offered
		const prospectPool = financial
			? compensationProspects(clubProspects)
			: clubProspects.sort((a, b) => a.value - b.value || b.pid - a.pid).slice(0, 4);
		const clubPicks = (await idb.cache.draftPicks.getAll()).filter(
			(pick) => pick.tid === tid && pick.round > 1,
		);
		// Gradual: a buyer return first, then limited seller prospects, later-round picks,
		// and (financial trades worth at least 5% of the plan) a moderate combination.
		const maxExtras = financial ? maxCompensationAssets(financial.materialShare) : 0;
		let result: TradeTeams | undefined;
		for (const stage of ["return", "prospect", "pick", "combined"] as const) {
			if (stage === "combined" && (!financial || maxExtras < 2)) continue;
			const prospectsOpen = stage === "prospect" || stage === "combined";
			const picksOpen = stage === "pick" || stage === "combined";
			// Compensation is added gradually: only when the plain return fails, and a
			// buyer without room is limited to deals that send salary back
			if (financial && stage === "return" && rebuildingVeteran) continue;
			if (financial && stage !== "return" && !financial.compensation) {
				trace?.push(`${abbrev}/${stage}: sem pressão orçamentária, o vendedor não paga para cortar salário`);
				continue;
			}
			const sellerPidsExcluded = (await idb.cache.players.indexGetAll("playersByTid", tid))
				.map((player) => player.pid)
				.concat(
					// Every minor leaguer outside this stage's pool stays out of the search
					clubMinors
						.filter((prospect) => !prospectsOpen || !prospectPool.some((candidate) => candidate.pid === prospect.pid))
						.map((prospect) => prospect.pid),
				);
			const sellerDpidsExcluded = (await idb.cache.draftPicks.getAll())
				.filter((pick) => pick.tid === tid && (!picksOpen || !clubPicks.some((candidate) => candidate.dpid === pick.dpid)))
				.map((pick) => pick.dpid);
			const stagedTeams: TradeTeams = [
				{ ...teams0[0], pidsExcluded: sellerPidsExcluded, dpidsExcluded: sellerDpidsExcluded },
				{ ...teams0[1], pidsExcluded },
			];
			const possible = await tradeSearch.makeItWork(stagedTeams, {
				holdUserConstant: !prospectsOpen && !picksOpen,
				maxAssetsToAdd: 2,
				valueChangeCalculator,
			});
			// Roster trims give only the player. A financial trade may add the seller's
			// compensation of this stage (lower-half prospects, or picks after round 1).
			const sellerAssetsOk = financial
				? possible !== undefined &&
					possible[0].pids.includes(p.pid) &&
					possible[0].pids.every(
						(pid) => pid === p.pid || (prospectsOpen && prospectPool.some((x) => x.pid === pid)),
					) &&
					possible[0].dpids.every(
						(dpid) => picksOpen && clubPicks.some((x) => x.dpid === dpid),
					) &&
					possible[0].pids.length - 1 + possible[0].dpids.length <= maxExtras &&
					// A rebuilding buyer takes a veteran's remaining years only with compensation
					(!rebuildingVeteran || possible[0].pids.length + possible[0].dpids.length > 1)
				: possible !== undefined &&
					possible[0].pids.length === 1 &&
					possible[0].dpids.length === 0;
			if (
				!possible ||
				!sellerAssetsOk ||
				(possible[1].pids.length === 0 && possible[1].dpids.length === 0) ||
				(await summary(possible)).warning
			) {
				trace?.push(`${abbrev}/${stage}: nenhum pacote aceitável pelos dois lados`);
				continue;
			}
			// Name the deal by what the seller really adds, not by the stage that found it
			const label = `${abbrev}/${
				possible[0].pids.length > 1 && possible[0].dpids.length > 0
					? "combined"
					: possible[0].pids.length > 1
						? "prospect"
						: possible[0].dpids.length > 0
							? "pick"
							: "return"
			}`;
			const sellerChange = await valueChangeCalculator.evaluate({
				tid,
				pidsAdd: possible[1].pids,
				pidsRemove: possible[0].pids,
				dpidsAdd: possible[1].dpids,
				dpidsRemove: possible[0].dpids,
				tradingPartnerTid: otherTid,
			});
			const buyerChange = await valueChangeCalculator.evaluate({
				tid: otherTid,
				pidsAdd: possible[0].pids,
				pidsRemove: possible[1].pids,
				dpidsAdd: possible[0].dpids,
				dpidsRemove: possible[1].dpids,
				tradingPartnerTid: tid,
			});
			const sellerBudget = await teamBaseballBudget(tid);
			const buyerBudget = await teamBaseballBudget(otherTid);
			// Check each remaining contract year using the net salaries actually moved.
			// A team already above plan may trade if the package lowers its obligations.
			const sellerPayrollFits = await tradePayrollFits(
				tid,
				possible[1].pids,
				possible[0].pids,
				sellerBudget,
			);
			const buyerPayrollFits = await tradePayrollFits(
				otherTid,
				possible[0].pids,
				possible[1].pids,
				buyerBudget,
			);
			if (
				sellerChange <= 0 ||
				buyerChange <= 0 ||
				!sellerPayrollFits ||
				!buyerPayrollFits
			) {
				trace?.push(
					`${label}: ${sellerChange <= 0 ? "vendedor perde valor" : buyerChange <= 0 ? "comprador sem benefício suficiente" : !buyerPayrollFits ? "estoura o orçamento do comprador em algum ano" : "estoura o orçamento do vendedor"}`,
				);
				continue;
			}
			if (financial) {
				const incoming = (
					await Promise.all(possible[1].pids.map((pid) => idb.cache.players.get(pid)))
				).filter((p2): p2 is Player => p2 !== undefined);
				const before = planRoster([...keep, p], size, dh()).total;
				const after = planRoster([...keep, ...incoming.filter((p2) => p2.tid >= 0)], size, dh()).total;
				const outgoing = (
					await Promise.all(possible[0].pids.map((pid) => idb.cache.players.get(pid)))
				).filter((p2): p2 is Player => p2 !== undefined);
				const savings = await effectiveSavings(tid, outgoing, incoming);
				const verdict = judgeFinancialTrade({
					problemScore: financial.problemScore,
					outgoingRemaining: remainingSalary([p], firstPayrollSeason()),
					netSavingsRemaining: savings.total,
					netSavingsNow: savings.now,
					sportingLoss: Math.max(0, before - after),
					remainingYears: Math.max(1, p.contract.exp - firstPayrollSeason() + 1),
					budget: sellerBudget,
					payroll: await getPayroll(tid, firstPayrollSeason()),
				});
				trace?.push(
					`${label}: recebe [${incoming.map((p2) => `${p2.firstName} ${p2.lastName} ${p2.contract.amount}/${p2.contract.exp}`).join(", ")}${possible[1].dpids.length ? ` + ${possible[1].dpids.length} pick(s)` : ""}] dá [${(await Promise.all(possible[0].pids.map((pid) => idb.cache.players.get(pid)))).map((p2) => (p2 ? `${p2.firstName} ${p2.lastName}` : "?")).join(", ")}${possible[0].dpids.length ? ` + ${possible[0].dpids.length} pick(s)` : ""}] valor vendedor ${sellerChange.toFixed(2)} comprador ${buyerChange.toFixed(2)} perda ${Math.max(0, before - after).toFixed(1)} -> ${verdict.ok ? "aceita" : verdict.reason}`,
				);
				if (!verdict.ok) continue;
			}
			result = possible;
			break;
		}
		if (!result) continue;

		await processTrade(result, undefined);
		valueChangeCalculator.invalidateCache({ teams: [tid, otherTid] });

		// Bought to play: past the minors age he joins the buyer's big club, otherwise he
		// would just be shopped again on the buyer's next roster check
		return true;
	}
	return false;
};

const rpRating = (p: Player) => {
	const r = p.ratings.at(-1)!;
	return r.ovrs?.RP ?? (r.pos === "RP" ? r.ovr : 0);
};

// Before using the generic surplus market, look for a direct roster upgrade:
// a club gives up a redundant, expensive player and receives a better, cheaper
// player at the same baseball role. Both teams must independently value the deal.
const tradeForBetterCheaperReliever = async (
	p: Player,
	keep: Player[],
	size: number,
	valueChangeCalculator: ValueChangeCalculator,
	trace?: string[],
) => {
	const outgoingRP = rpRating(p);
	if (outgoingRP <= 0 || isUntradable(p).untradable) {
		return false;
	}

	const tid = ownerTid(p)!;
	const season = g.get("season");
	const formerTeams = recentFormerTeams(p, season);
	const { now } = await marketCalendar();
	const teams = await idb.cache.teams.getAll();
	const targets: { tid: number; p: Player; fit: number }[] = [];
	for (const t of teams) {
		if (t.disabled || t.tid === tid || !isAiTeam(t.tid) || formerTeams.has(t.tid)) continue;
		const roster = await idb.cache.players.indexGetAll("playersByTid", t.tid);
		const possibleTargets = roster
			.filter(
				(candidate) =>
					!isUntradable(candidate).untradable &&
					!recentlyTraded(candidate, now) &&
					rpRating(candidate) >= outgoingRP + 3 &&
					candidate.contract.amount < p.contract.amount,
			)
			.sort(
				(a, b) =>
					rpRating(b) - rpRating(a) ||
					a.contract.amount - b.contract.amount ||
					a.pid - b.pid,
			)
			.slice(0, 3);
		for (const candidate of possibleTargets) {
			const sellerFit = rosterImprovement(keep, candidate, size, dh());
			const buyerFit = rosterImprovement(
				roster.filter((p2) => p2.pid !== candidate.pid),
				p,
				g.get("maxRosterSize"),
				dh(),
			);
			if (sellerFit >= MIN_NEED_GAIN && buyerFit >= MIN_NEED_GAIN) {
				targets.push({ tid: t.tid, p: candidate, fit: sellerFit + buyerFit });
			}
		}
	}
	targets.sort(
		(a, b) =>
			b.fit - a.fit ||
			rpRating(b.p) - rpRating(a.p) ||
			a.p.contract.amount - b.p.contract.amount ||
			a.p.pid - b.p.pid,
	);

	trace?.push(
		`troca direta por reliever melhor e mais barato: ${targets.length ? targets.slice(0, 12).map((x) => `${x.p.firstName} ${x.p.lastName} (${x.p.contract.amount})`).join(", ") : "nenhum alvo com encaixe para os dois lados"}`,
	);
	for (const target of targets.slice(0, 12)) {
		const buyerChange = await valueChangeCalculator.evaluate({
			tid: target.tid,
			pidsAdd: [p.pid],
			pidsRemove: [target.p.pid],
			dpidsAdd: [],
			dpidsRemove: [],
			tradingPartnerTid: tid,
		});
		if (buyerChange <= 0) continue;

		const sellerChange = await valueChangeCalculator.evaluate({
			tid,
			pidsAdd: [target.p.pid],
			pidsRemove: [p.pid],
			dpidsAdd: [],
			dpidsRemove: [],
			tradingPartnerTid: target.tid,
		});
		if (sellerChange <= 0) continue;

		const result: TradeTeams = [
			{ tid, pids: [p.pid], dpids: [], pidsExcluded: [], dpidsExcluded: [] },
			{
				tid: target.tid,
				pids: [target.p.pid],
				dpids: [],
				pidsExcluded: [],
				dpidsExcluded: [],
			},
		];
		if ((await summary(result)).warning) continue;
		if (
			!(await tradePayrollFits(
				tid,
				[target.p.pid],
				[p.pid],
				await teamBaseballBudget(tid),
			)) ||
			!(await tradePayrollFits(
				target.tid,
				[p.pid],
				[target.p.pid],
				await teamBaseballBudget(target.tid),
			))
		)
			continue;

		await processTrade(result, undefined);
		valueChangeCalculator.invalidateCache({ teams: [tid, target.tid] });
		return true;
	}
	return false;
};

// Performance note: the schedule does not change while a simulated day is being processed. Reuse the
// calendar for every club checked on that day; play.ts invalidates this at the start
// of the next day. This avoids one full IndexedDB schedule scan per AI team.
let marketCalendarCache:
	| { season: number; phase: number; value: Awaited<ReturnType<typeof readMarketCalendar>> }
	| undefined;

const readMarketCalendar = async () => {
	const season = g.get("season");
	const phase = g.get("phase");
	let day = 0;
	let fraction = 0;
	let daysToDeadline: number | undefined;
	let lastDay = 0;
	if (phase === PHASE.REGULAR_SEASON || phase === PHASE.AFTER_TRADE_DEADLINE) {
		const schedule = await idb.cache.schedule.getAll();
		day = schedule[0]?.day ?? 0;
		lastDay = schedule.at(-1)?.day ?? day;
		fraction = lastDay > 0 ? day / lastDay : 1;
		const deadline = schedule.find((game) => game.awayTid === -3 && game.homeTid === -3);
		if (deadline) daysToDeadline = deadline.day - day;
		lastDay = Math.max(lastDay, day);
	}
	// Days since the current window opened, used to spread clubs over its first days
	const windowDay =
		daysToDeadline !== undefined && daysToDeadline <= DEADLINE_WINDOW_DAYS
			? DEADLINE_WINDOW_DAYS - daysToDeadline
			: day - Math.ceil(0.4 * lastDay);
	return { day, fraction, daysToDeadline, windowDay, now: dayStamp(season, phase, day) };
};

export const invalidateMarketCalendarCache = () => {
	marketCalendarCache = undefined;
};

export const marketCalendar = async () => {
	const season = g.get("season");
	const phase = g.get("phase");
	if (marketCalendarCache?.season === season && marketCalendarCache.phase === phase) {
		return marketCalendarCache.value;
	}
	const value = await readMarketCalendar();
	marketCalendarCache = { season, phase, value };
	return value;
};

const freshWatch = (season: number, month: string): ContractWatch => ({
	season,
	month,
	entries: {},
	runs: {},
});

// Monthly monitoring: every roster contract of an AI club is profiled against the
// league market, compared with the best cheap replacement the club already has access
// to (own minors, cheap free agents) and marked keep or shop. Nothing is offered here.
export const evaluateContracts = async (tid: number) => {
	const season = g.get("season");
	const roster = await idb.cache.players.indexGetAll("playersByTid", tid);
	const size = g.get("maxRosterSize");
	const baseline = planRoster(roster, size, dh());
	const leaguePlayers = await idb.cache.players.getAll();
	const budget = await teamBaseballBudget(tid);
	const payroll = await getPayroll(tid, firstPayrollSeason());
	const replacements = leaguePlayers
		.filter(
			(p) =>
				p.injury.gamesRemaining === 0 &&
				((p.tid === PLAYER.MINORS && p.minorTid === tid) ||
					(p.tid === PLAYER.FREE_AGENT &&
						p.contract.amount <= g.get("minContract") * 2)),
		)
		.sort((a, b) => b.value - a.value)
		.slice(0, 15);
	const entries: Record<number, ContractWatchEntry> = {};
	for (const p of roster) {
		const r = p.ratings.at(-1)!;
		if (isUntradable(p).untradable || (minorsEligible(p) && r.pot - r.ovr >= 8)) {
			continue;
		}
		const without = roster.filter((p2) => p2.pid !== p.pid);
		const withoutPlan = planRoster(without, size, dh());
		const impact = Math.max(0, baseline.total - withoutPlan.total);
		const profile = profileProblemContract(p, leaguePlayers, impact, season);
		if (profile.problemScore < PROBLEM_SCORE) continue;
		let bestTotal = withoutPlan.total;
		for (const c of replacements) {
			if (c.contract.amount >= p.contract.amount) continue;
			bestTotal = Math.max(bestTotal, planRosterAddition(without, c, withoutPlan).total);
		}
		const netLoss = Math.max(0, baseline.total - bestTotal);
		const remainingYears = Math.max(0, p.contract.exp - firstPayrollSeason() + 1);
		if (remainingYears === 0) continue; // contract ends with the season just played
		const savings = await effectiveSavings(tid, [p], []);
		const decision = decideContract({
			problemScore: profile.problemScore,
			impact,
			netLoss,
			salary: savings.now,
			futureSavings: savings.total - savings.now,
			remainingYears,
			budget,
			payroll,
		});
		entries[p.pid] = {
			...decision,
			pid: p.pid,
			problemScore: profile.problemScore,
			marketRatio: profile.marketRatio,
			impact,
			recentWar: profile.recentWar,
			remainingYears,
			month: "",
		};
	}
	return { entries, budget, payroll, rosterSize: roster.length };
};

// Execution happens only in the pre-season, mid-season and deadline windows. A shop
// decision is tried once per context: a new window, an injury, the club going over
// its plan or a roster need. Monitoring results and attempts are saved on the team,
// so reloading the league neither repeats attempts nor loses the protection.
export const shopRosterSurplus = async (
	tid: number,
	// window: audits and tests choose the market window instead of the calendar
	options: { trace?: string[]; force?: boolean; window?: ContractWindow } = {},
) => {
	if (!isAiTeam(tid) || !tradesAllowed()) return;
	const t = (await idb.cache.teams.get(tid)) as WatchedTeam | undefined;
	if (!t) return;
	const season = g.get("season");
	const phase = g.get("phase");
	const calendar = await marketCalendar();
	const month = contractMonth(phase, calendar.day);
	const window =
		options.window ?? contractWindow(phase, calendar.fraction, calendar.daysToDeadline);

	let watch = t.aiContractWatch;
	if (!watch || watch.season !== season) watch = freshWatch(season, "");
	const windowKey = window ? `${window}` : undefined;
	const windowStarting = windowKey !== undefined && watch.runs[windowKey] === undefined;
	let changed = false;
	if (watch.month !== month || windowStarting || options.force) {
		const evaluation = await evaluateContracts(tid);
		const previous = watch.entries;
		watch = { ...watch, month, entries: {} };
		for (const entry of Object.values(evaluation.entries)) {
			watch.entries[entry.pid] = {
				...entry,
				month,
				tried: previous[entry.pid]?.tried,
				result: previous[entry.pid]?.result,
			};
		}
		changed = true;
	}

	// Clubs are spread over the first days of an in-season window (club tid % WINDOW_SPREAD_DAYS), so the
	// whole league does not search the market on the same simulated day
	const myTurn =
		window === "pre" ||
		options.window !== undefined ||
		calendar.windowDay >= tid % WINDOW_SPREAD_DAYS;
	if (myTurn && windowKey !== undefined && (watch.runs[windowKey] ?? 0) < MAX_WINDOW_RUNS) {
		const firstRun = (watch.runs[windowKey] ?? 0) === 0;
		const roster = await idb.cache.players.indexGetAll("playersByTid", tid);
		const budget = await teamBaseballBudget(tid);
		const payroll = await getPayroll(tid, firstPayrollSeason());
		const overBudget = payroll > budget;
		const pressure = payroll >= budget * PRESSURE_PAYROLL;
		const candidates: { p: Player; entry: ContractWatchEntry; context: string }[] = [];
		const dumpsOk = extraDumpAllowed(watch.dumps ?? 0, payroll, budget);
		if (!dumpsOk) {
			options.trace?.push(`já fez ${watch.dumps} troca(s) financeira(s) nesta temporada e está dentro do plano: só monitora`);
		}
		for (const entry of dumpsOk ? Object.values(watch.entries) : []) {
			const p = roster.find((p2) => p2.pid === entry.pid);
			if (!p) continue;
			if (entry.decision !== "shop") {
				options.trace?.push(`${p.firstName} ${p.lastName}: mantido (${entry.reason})`);
				continue;
			}
			// Exception: a club more than 10% over its plan may move him anyway
			if (recentlyTraded(p, calendar.now) && payroll <= budget * 1.1) {
				options.trace?.push(`${p.firstName} ${p.lastName}: trocado há menos de ${RESALE_PROTECTION_DAYS} dias, não é revendido`);
				continue;
			}
			const context = contractContext({
				window: window!,
				injured: p.injury.gamesRemaining > 0,
				overBudget,
				pressure,
			});
			if (entry.tried === context) {
				options.trace?.push(`${p.firstName} ${p.lastName}: mesma tentativa sem mudança de contexto (${context})`);
				continue;
			}
			// One market run per window looks at the top 2. Later runs in the same window are
			// only for a contract already tried here whose context changed (injury, budget).
			if (!firstRun && !entry.tried?.startsWith(`${window}|`)) continue;
			candidates.push({ p, entry, context });
		}
		candidates.sort((a, b) => b.entry.merit - a.entry.merit);
		const attempts = candidates.slice(0, MAX_SURPLUS_SHOPPING_ATTEMPTS);
		if (attempts.length > 0) {
			watch.runs[windowKey] = (watch.runs[windowKey] ?? 0) + 1;
			const size = g.get("maxRosterSize");
			const calculator = new ValueChangeCalculator();
			for (const { p, entry, context } of attempts) {
				const keep = roster.filter((p2) => p2.pid !== p.pid);
				options.trace?.push(
					`${p.firstName} ${p.lastName}: procura de comprador (${context}, mérito ${entry.merit.toFixed(2)}, perda líquida ${entry.netLoss.toFixed(1)}, salário ${(entry.savingsShare * 100).toFixed(1)}% do plano)`,
				);
				// Any position, any buyer first; the reliever-for-reliever swap is a fallback
				const traded =
					(await tradeSurplus(p, keep, size, calculator, {
						problemScore: entry.problemScore,
						compensation: entry.compensation,
						materialShare: entry.savingsShare + entry.futureShare,
						trace: options.trace,
					})) ||
					(await tradeForBetterCheaperReliever(p, keep, size, calculator, options.trace));
				entry.tried = context;
				entry.result = traded ? "trocado" : "sem negócio vantajoso";
				options.trace?.push(`${p.firstName} ${p.lastName}: ${entry.result}`);
				if (traded) {
					delete watch.entries[p.pid];
					watch.dumps = (watch.dumps ?? 0) + 1;
					// The reference applies inside one run too
					if (!extraDumpAllowed(watch.dumps, await getPayroll(tid, firstPayrollSeason()), budget)) break;
				}
				calculator.invalidateCache({ teams: [tid] });
			}
		} else if (watch.runs[windowKey] === undefined) {
			// Window seen with nothing to try: no evaluation is repeated until the month changes
			watch.runs[windowKey] = 0;
		}
		changed = true;
	}

	if (changed) {
		// Re-read: a trade above may have changed the team object in the cache
		const current = (await idb.cache.teams.get(tid)) as WatchedTeam | undefined;
		if (current) {
			current.aiContractWatch = watch;
			await idb.cache.teams.put(current);
		}
	}
	return watch;
};

export const trimBaseballRoster = async (
	playersInput: Player[],
	numToDrop: number,
) => {
	const tid = playersInput[0]?.tid;
	const target = Math.max(0, playersInput.length - numToDrop);
	const released: number[] = [];
	if (tid === undefined || numToDrop <= 0) {
		return released;
	}

	let players = playersInput;
	let tradeAttempts = 0;
	let trades = 0;
	const valueChangeCalculator = new ValueChangeCalculator();
	// In free agency and the preseason clubs reshape their rosters all at once (signings, development), and shopping
	// every surplus player to every other club made those days take minutes. AI-AI trades still happen through the
	// daily trade check; here the surplus just goes to the minors.
	const canTrade =
		tradesAllowed() &&
		g.get("phase") !== PHASE.FREE_AGENCY &&
		g.get("phase") !== PHASE.PRESEASON;

	// A trade can bring back a player the club needs, so plan again until the roster fits
	for (let round = 0; round < 5 && players.length > target; round++) {
		const plan = planRoster(players, target, dh());
		const keep = players.filter((p) => plan.selected.has(p));
		// Most valuable first: they are the ones with a market
		const surplus = players
			.filter((p) => !plan.selected.has(p))
			.sort((a, b) => b.value - a.value);

		for (const p of surplus) {
			const r = p.ratings.at(-1)!;
			const developing = minorsEligible(p) && r.pot - r.ovr >= 5;

			// Prospects still growing are kept in the farm system
			if (developing) {
				await sendToMinors(p);
				continue;
			}

			if (
				canTrade &&
				trades < MAX_TRADES &&
				tradeAttempts < MAX_TRADE_ATTEMPTS
			) {
				tradeAttempts += 1;
				if (await tradeSurplus(p, keep, target, valueChangeCalculator)) {
					trades += 1;
					continue;
				}
			}

			// Keep surplus players in the minors regardless of age when no trade is found.
			await sendToMinors(p);
		}

		players = await idb.cache.players.indexGetAll("playersByTid", tid);
	}

	return released;
};

// Minimum minor league system (setting "Min Minor League Players", default 20; composition in
// minorsComposition.baseball.ts: 11 position players, 9 pitchers, catcher + alternative, 5 starters, the 8 field
// positions covered). An AI organization signs, straight into its minors, free agents who ask for the minimum and are
// young enough for the farm system, one at a time, always the one who reduces its shortages the most (a system above
// the minimum can still be short on composition; there is no maximum and nobody is released). It never demotes a big
// leaguer and never creates a player; if no free agent helps, it stops and tries again on the next roster check.
// Called after the big-club roster is filled, so that always comes first. The user's organization is never filled.
const MAX_MINORS_SIGNINGS_PER_CHECK = 25;
// A free agent's traits don't change during a roster check; computed once for all organizations
const candidateTraitsCache = new WeakMap<Player, CandidateTraits>();
export const fillMinorsMinimum = async (
	tid: number,
	minFreeAgents: Player[],
) => {
	const min = g.get("minMinorsSize") ?? 0;
	const phase = g.get("phase");
	if (
		min <= 0 ||
		!isAiTeam(tid) ||
		!(
			[
				PHASE.PRESEASON,
				PHASE.REGULAR_SEASON,
				PHASE.AFTER_TRADE_DEADLINE,
				PHASE.FREE_AGENCY,
			] as number[]
		).includes(phase)
	) {
		return [];
	}
	const minors = (
		await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS)
	).filter((p) => p.minorTid === tid);
	const signed: Player[] = [];
	while (signed.length < MAX_MINORS_SIGNINGS_PER_CHECK) {
		const before = minorsStatus(minors, min);
		if (!hasShortage(before)) {
			break;
		}
		let best: { p: Player; gain: number } | undefined;
		for (const p of minFreeAgents) {
			if (p.tid !== PLAYER.FREE_AGENT || !minorsEligible(p)) continue;
			let traits = candidateTraitsCache.get(p);
			if (!traits) {
				traits = candidateTraits(p);
				candidateTraitsCache.set(p, traits);
			}
			const gain = quickReduction(before, traits);
			if (
				gain > 0 &&
				(!best ||
					gain > best.gain ||
					(gain === best.gain &&
						(p.value > best.p.value ||
							(p.value === best.p.value && p.pid < best.p.pid))))
			) {
				best = { p, gain };
			}
		}
		if (!best) {
			break; // nobody available helps: still short, retried on the next roster check
		}
		const p = best.p;
		minFreeAgents.splice(minFreeAgents.indexOf(p), 1);
		await player.sign(p, tid, p.contract, phase);
		await sendToMinors(p);
		minors.push(p);
		signed.push(p);
	}
	return signed.map((p) => p.pid);
};

// Every AI organization at once, fairly: the free agents who fit the minors are scarce (a new league has about
// 115 for 29 organizations that need 20 each), so signing them organization by organization in tid order gave
// everything to the first few. Here organizations take turns, one signing per turn, the neediest first, each
// picking with the same rule as fillMinorsMinimum. Same limits: no maximum, nobody released, nobody created, the
// user's organization untouched, at most MAX_MINORS_SIGNINGS_PER_CHECK per organization per check. Stops as soon
// as a full round signs nobody (no loop when the market is empty) and reports what is still missing.
const shortageScore = (s: MinorsStatus) =>
	3 * s.short.fielders +
	3 * s.short.pitchers +
	2 * s.short.positions +
	2 * s.short.catchers +
	2 * s.short.starters +
	s.short.total;
export const fillMinorsMinimumFair = async (
	tids: number[],
	minFreeAgents: Player[],
) => {
	const min = g.get("minMinorsSize") ?? 0;
	const phase = g.get("phase");
	const report = {
		signed: [] as number[],
		unmet: [] as { tid: number; short: MinorsStatus["short"] }[],
	};
	if (
		min <= 0 ||
		!(
			[
				PHASE.PRESEASON,
				PHASE.REGULAR_SEASON,
				PHASE.AFTER_TRADE_DEADLINE,
				PHASE.FREE_AGENCY,
			] as number[]
		).includes(phase)
	) {
		return report;
	}
	const ai = tids.filter(isAiTeam);
	const allMinors = await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.MINORS,
	);
	const orgs = ai.map((tid) => ({
		tid,
		minors: allMinors.filter((p) => p.minorTid === tid),
		signed: 0,
	}));
	for (;;) {
		const turn = orgs
			.map((o) => ({ o, status: minorsStatus(o.minors, min) }))
			.filter(
				(x) =>
					hasShortage(x.status) && x.o.signed < MAX_MINORS_SIGNINGS_PER_CHECK,
			)
			.sort(
				(a, b) =>
					shortageScore(b.status) - shortageScore(a.status) ||
					a.o.tid - b.o.tid,
			);
		let any = false;
		for (const { o } of turn) {
			// Recomputed: an earlier signing this round may have taken the best candidate
			const before = minorsStatus(o.minors, min);
			let best: { p: Player; gain: number } | undefined;
			for (const p of minFreeAgents) {
				if (p.tid !== PLAYER.FREE_AGENT || !minorsEligible(p)) continue;
				let traits = candidateTraitsCache.get(p);
				if (!traits) {
					traits = candidateTraits(p);
					candidateTraitsCache.set(p, traits);
				}
				const gain = quickReduction(before, traits);
				if (
					gain > 0 &&
					(!best ||
						gain > best.gain ||
						(gain === best.gain &&
							(p.value > best.p.value ||
								(p.value === best.p.value && p.pid < best.p.pid))))
				) {
					best = { p, gain };
				}
			}
			if (!best) continue;
			const p = best.p;
			minFreeAgents.splice(minFreeAgents.indexOf(p), 1);
			await player.sign(p, o.tid, p.contract, phase);
			await sendToMinors(p);
			o.minors.push(p);
			o.signed += 1;
			report.signed.push(p.pid);
			any = true;
		}
		if (!any) break;
	}
	for (const o of orgs) {
		const status = minorsStatus(o.minors, min);
		if (hasShortage(status))
			report.unmet.push({ tid: o.tid, short: status.short });
	}
	return report;
};

// Called after promotions. Failed searches are retried weekly, oldest review first,
// without spending other assets or automatically releasing the blocked player.
export const moveOutAgedMinors = async (tid: number): Promise<number[]> => {
	if (!isAiTeam(tid) || !tradesAllowed()) return [];
	const minors = (await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS))
		.filter((p) => p.minorTid === tid) as MinorMarketPlayer[];
	const roster = await idb.cache.players.indexGetAll("playersByTid", tid);
	const benchmarks = majorLeagueBenchmarks(await idb.cache.players.indexGetAll("playersByTid", [0, Infinity]));
	const calendar = await marketCalendar();
	const day = g.get("phase") === PHASE.FREE_AGENCY ? -g.get("daysLeft") : calendar.day;
	const reviewOrder = (p: MinorMarketPlayer) => p.minorMarketReview?.season === g.get("season") && p.minorMarketReview.phase === g.get("phase") ? p.minorMarketReview.day : -Infinity;
	const candidates = minors.filter((p) => minorMarketReviewDue(p, day) &&
		!isUntradable(p).untradable && minorLeaguePlan(p, roster, minors, benchmarks) === "blocked")
		.sort((a, b) => (reviewOrder(a) === reviewOrder(b) ? 0 : reviewOrder(a) < reviewOrder(b) ? -1 : 1) || b.value - a.value || a.pid - b.pid);
	const calculator = new ValueChangeCalculator();
	for (const p of candidates.slice(0, MAX_SURPLUS_SHOPPING_ATTEMPTS)) {
		// Re-read after any trade: the organization and its coverage may have changed.
		const current = await idb.cache.players.get(p.pid);
		if (!current || current.tid !== PLAYER.MINORS || current.minorTid !== tid) continue;
		const org = (await organizationPlayers(tid));
		const active = org.filter((p2) => p2.tid === tid);
		if (minorLeaguePlan(current, active, org.filter((p2) => p2.tid === PLAYER.MINORS), benchmarks) !== "blocked") continue;
		(current as MinorMarketPlayer).minorMarketReview = { season: g.get("season"), phase: g.get("phase"), day };
		await idb.cache.players.put(current);
		await tradeSurplus(current, active, g.get("maxRosterSize"), calculator);
	}
	return [];
};

export const promoteBaseballNeeds = async (tid: number, minors: Player[]) => {
	let roster = await idb.cache.players.indexGetAll("playersByTid", tid);
	const max = g.get("maxRosterSize");
	let available = minors.filter(
		(p) =>
			p.tid === PLAYER.MINORS &&
			p.minorTid === tid &&
			p.injury.gamesRemaining === 0,
	);
	const released: number[] = [];
	// Gradual repair, not a league-wide instant redistribution. Call before FA
	// so a club uses its own useful prospects before buying redundant players.
	// Open roster spots are filled at once, so a raised limit doesn't wait for free agency.
	const numChecks = Math.max(3, max - roster.length);
	for (let i = 0; i < numChecks && available.length; i++) {
		// No budget check: the club already pays its own minor leaguers, so a call-up adds no new money. The budget
		// only limits new money (signings, trades that add salary).
		// Any real gain is enough: beating the current backup at any position counts
		const p = rankRosterCandidates(roster, available, max, dh(), 0.5)[0];
		if (!p) break;
		await rememberMinorLeagueCallup(p);
		p.tid = tid;
		delete p.minorTid;
		p.contract.exp = Math.max(p.contract.exp, g.get("season"));
		// Keeps his number unless an active teammate wears it now
		player.setJerseyNumber(p, await player.genJerseyNumber(p));
		await idb.cache.players.put(p);
		roster.push(p);
		if (roster.length > max) {
			released.push(...(await trimBaseballRoster(roster, roster.length - max)));
			roster = roster.filter((p2) => p2.tid === tid);
		}
		available = available.filter((p2) => p2 !== p);
	}
	return released;
};

// Playoffs: the daily roster check (signings, trades, minors fill-up) is off, but an AI club still uses its own
// organization. A player back from injury or a better minor leaguer is called up and the surplus goes down to the
// minors; nobody is signed, traded or released (trades are closed in the playoffs, see tradesAllowed). Only clubs
// still in the bracket, since nobody else plays.
export const playoffCallups = async () => {
	const minors = await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS);
	const playoffTids = new Set(
		(
			await idb.cache.teamSeasons.indexGetAll("teamSeasonsBySeasonTid", [
				[g.get("season")],
				[g.get("season"), "Z"],
			])
		)
			.filter((ts) => ts.playoffRoundsWon >= 0)
			.map((ts) => ts.tid),
	);
	for (const t of await idb.cache.teams.getAll()) {
		if (t.disabled || !playoffTids.has(t.tid) || !isAiTeam(t.tid) || g.get("forceHistoricalRosters")) {
			continue;
		}
		await promoteBaseballNeeds(t.tid, minors);
		await assignBaseballPositions(t.tid);
		await rosterAutoSort(t.tid);
	}
};

const bestFieldPos = (ovrs: Record<string, number>) =>
	Object.entries(ovrs)
		.filter(([pos]) => !["SP", "RP", "DH"].includes(pos))
		.sort((a, b) => b[1] - a[1])[0];

// Keep the listed position in line with how the club uses a player. Only the label
// moves, along with the ovr/pot shown for it; the underlying ratings stay as they are.
const setPos = async (p: Player, pos: string) => {
	const r = p.ratings.at(-1)!;
	const ovrs = (r as any).ovrs as Record<string, number> | undefined;
	const pots = (r as any).pots as Record<string, number> | undefined;
	if (r.pos === pos || ovrs?.[pos] === undefined) {
		return false;
	}
	// The roster assignment can use a spare arm as emergency field coverage.
	// That is not a conversion: never permanently replace his pitching position
	// with a much weaker batting position (or do the reverse to a hitter).
	const fromPitcher = r.pos === "SP" || r.pos === "RP";
	const toPitcher = pos === "SP" || pos === "RP";
	// Real positions are kept in the first seasons of a league; only starter/reliever roles still move
	if (realPositionsProtected() && !(fromPitcher && toPitcher)) {
		return false;
	}
	// A two-way player stays a pitcher who also hits; the AI never converts him
	if (p.twoWay && fromPitcher !== toPitcher) {
		return false;
	}
	if (fromPitcher !== toPitcher) {
		const currentRoleOvr = fromPitcher
			? Math.max(ovrs.SP ?? 0, ovrs.RP ?? 0)
			: (bestFieldPos(ovrs)?.[1] ?? r.ovr);
		if (ovrs[pos]! < currentRoleOvr) {
			return false;
		}
	}
	r.pos = pos;
	r.ovr = ovrs[pos]!;
	if (pots?.[pos] !== undefined) {
		r.pot = pots[pos]!;
	}
	if (typeof p.pos === "string") {
		// League file players are pinned through p.pos, see player.develop
		p.pos = pos;
	}
	player.updateValues(p);
	await idb.cache.players.put(p);
	return true;
};

export const assignBaseballPositions = async (tid: number) => {
	if (!isAiTeam(tid) || g.get("forceHistoricalRosters")) {
		return;
	}
	const players = await organizationPlayers(tid);
	const roster = players.filter((p) => p.tid === tid);
	const plan = planRoster(roster, g.get("maxRosterSize"), dh());

	for (const [slotIndex, i] of plan.assignment.entries()) {
		if (i < 0) {
			continue;
		}
		const p = roster[i]!;
		const role = plan.slots[slotIndex]!.pos;
		const r = p.ratings.at(-1)!;
		const ovrs = (r as any).ovrs as Record<string, number>;
		const pots = (r as any).pots as Record<string, number> | undefined;

		if (role === "SP" || role === "RP") {
			// A starter still developing stays a starter even while he relieves
			const growingStarter =
				role === "RP" &&
				r.pos === "SP" &&
				age(p) <= 25 &&
				(pots?.SP ?? 0) - ovrs.SP! >= 8;
			if (!growingStarter) {
				await setPos(p, role);
			}
		} else if (ovrs[role] !== undefined && role !== "DH") {
			await setPos(p, role);
		} else {
			// DH and depth roles: keep the label unless it is a position he can't really play
			const pitcherLabel = r.pos === "SP" || r.pos === "RP";
			if (pitcherLabel && role === "ANY" && isPitcherCapable(r)) {
				// Spare arm kept as depth
				continue;
			}
			const best = bestFieldPos(ovrs);
			if (best && (pitcherLabel || (ovrs[r.pos] ?? 0) < best[1] - 8)) {
				await setPos(p, best[0]);
			}
		}
	}

	// Minor leaguers keep developing at whichever position suits them best
	for (const p of players) {
		if (p.tid !== PLAYER.MINORS) {
			continue;
		}
		const r = p.ratings.at(-1)!;
		const ovrs = (r as any).ovrs as Record<string, number>;
		if (r.pos === "SP" || r.pos === "RP") {
			continue;
		}
		const best = bestFieldPos(ovrs);
		if (best && (ovrs[r.pos] ?? 0) <= best[1] - RELABEL_MARGIN) {
			await setPos(p, best[0]);
		}
	}
};

// A position player's label is fixed when he is generated and never follows his development
// (player.develop keeps it on purpose). So many players who now play SS best still read 2B,
// CF or 3B. Free agents get the label of their best field position once it is clearly ahead,
// which keeps near ties from flipping back and forth and never labels anyone SS who isn't.
const RELABEL_MARGIN = 3;
export const relabelFreeAgents = async () => {
	for (const p of await idb.cache.players.indexGetAll(
		"playersByTid",
		PLAYER.FREE_AGENT,
	)) {
		const r = p.ratings.at(-1)!;
		const ovrs = (r as any).ovrs as Record<string, number> | undefined;
		if (!ovrs || r.pos === "SP" || r.pos === "RP") {
			continue;
		}
		const best = bestFieldPos(ovrs);
		if (best && (ovrs[r.pos] ?? 0) <= best[1] - RELABEL_MARGIN) {
			await setPos(p, best[0]);
		}
	}
};

